import { WasteHandoverPlanModel } from "services/Main/wasteHandoverPlan_services";
import { EditableFormInstance, EditableProTable, ProColumns, nanoid } from "@ant-design/pro-components";
import { Form, Input, InputNumber, Select, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import { customTableClassName } from "base/ui/components/CustomTable";
import { formatDecimalNumber } from "utils/formatHelper";
import { uiStore } from "stores/root_store";
import { CardBox } from "./cardBox";

interface HandoverInfoEditProps {
  editTableForm: any;
  editableFormRef: React.MutableRefObject<EditableFormInstance | undefined>;
  dataSource: WasteHandoverPlanModel[];
  wasteOptions: { label: string; value: string; productName?: string; wasteGroupingContractId?: string }[];
  contractOptionsMap: Map<string, string>;
  storageOptions: { label: string; value: string }[];
  setDataTable: React.Dispatch<React.SetStateAction<WasteHandoverPlanModel[]>>;
  isThemSuaChatThai?: boolean; // Quyền thêm/sửa chất thải
  disabledAll?: boolean; // Khóa toàn bộ khi phiếu đã hủy
}

const HandoverInfoEdit: React.FC<HandoverInfoEditProps> = ({
  editTableForm,
  editableFormRef,
  dataSource,
  wasteOptions,
  contractOptionsMap,
  storageOptions,
  setDataTable,
  isThemSuaChatThai = false,
  disabledAll = false,
}) => {
  const { t } = useTranslation();

  // Helper function để check xem item có wasteHandoverPlanId hợp lệ không (không phải GUID rỗng)
  const hasValidWasteHandoverPlanId = (record: any): boolean => {
    const planId = record?.wasteHandoverPlanId;
    if (!planId) return false;
    // Check nếu không phải GUID rỗng
    return planId !== '00000000-0000-0000-0000-000000000000' && planId.trim() !== '';
  };

  // Helper function để check xem item có được phép sửa không
  const canEditItem = (record: any): boolean => {
    if (disabledAll) return false;
    // Nếu không có quyền thêm/sửa chất thải thì không cho sửa
    if (!isThemSuaChatThai) return false;
    // Nếu trạng thái là "Đã bàn giao" (DBG) hoặc "Từ chối bàn giao" (TCBG) thì không cho sửa
    const deliveryStatus = (record as any)?.deliveryStatus || '';
    if (deliveryStatus === 'DBG' || deliveryStatus === 'TCBG') return false;
    // Nếu item đã có wasteHandoverPlanId hợp lệ (từ kế hoạch) thì không cho sửa
    if (hasValidWasteHandoverPlanId(record)) return false;
    return true;
  };

  const columns: ProColumns<WasteHandoverPlanModel>[] = [
    {
      title: t('waste-handover-plan.stt'),
      dataIndex: 'stt',
      key: 'stt',
      align: 'center',
      width: 60,
      className: 'min-w-[60px]',
      fixed: 'left',
      editable: (_, record) => {
        if (disabledAll) return false;
        // STT cho phép sửa nếu có quyền và không phải trạng thái DBG/TCBG
        if (!isThemSuaChatThai) return false;
        const deliveryStatus = (record as any)?.deliveryStatus || '';
        if (deliveryStatus === 'DBG' || deliveryStatus === 'TCBG') return false;
        return true;
      },
      renderFormItem: (_, config) => {
        const { record } = config;
        return (
          <InputNumber
            min={1}
            style={{ width: '100%', textAlign: 'center' }}
            placeholder={t('waste-handover-plan.stt')}
            value={(record as any)?.stt}
            controls={false}
          />
        );
      },
      formItemProps: (_, config) => {
        return {
          rules: [
            { required: true, message: 'Vui lòng nhập STT' },
          ],
        };
      },
      render: (_, record) => {
        return <div style={{ textAlign: 'center', padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(record as any)?.stt || '-'}</div>;
      },
    },
    // phân nhóm chất thải/ phế liệu
    {
      title: t('waste-handover-plan-register.waste-subgroup-contract'),
      key: "wasteGroupingContractName",
      dataIndex: "wasteGroupingContractName",
      width: 180,
      className: 'min-w-[180px]',
      readonly: true,
      fixed: 'left',
      render: (_, record) => {
        // Nếu có wasteGroupingContractName trong record thì dùng luôn
        if ((record as any)?.wasteGroupingContractName) {
          return (record as any).wasteGroupingContractName;
        }
        // Nếu không có, thử tìm từ productCode và contractOptionsMap
        const productCode = (record as any)?.productCode;
        if (productCode && contractOptionsMap.size > 0) {
          const wasteOption = wasteOptions.find(opt => String(opt.value) === String(productCode));
          if (wasteOption?.wasteGroupingContractId) {
            const contractName = contractOptionsMap.get(wasteOption.wasteGroupingContractId);
            if (contractName) {
              return contractName;
            }
          }
        }
        return '-';
      },
    },
    // chất thải/ phế liệu
    {
      title: t('waste-handover-plan-register.waste-scrap'),
      key: "waste",
      dataIndex: "productCode",
      width: 400,
      className: 'min-w-[400px]',
      fixed: 'left',
      editable: (_, record) => {
        // Check xem item có được phép sửa không (không cho sửa nếu đã có wasteHandoverPlanId hợp lệ)
        return canEditItem(record);
      },
      renderFormItem: (_, config) => {
        const { record } = config;
        // Dùng key giống rowKey function: ưu tiên wasteHandoverRegistrationDetailId, sau đó wasteHandoverPlanId
        const recordKey = (record as any)?.wasteHandoverRegistrationDetailId || record?.wasteHandoverPlanId;


        // Get all selected productCodes except for the current row
        // const selectedProductCodes = dataSource
        //   .filter(item => {
        //     const itemKey = (item as any)?.wasteHandoverRegistrationDetailId || item.wasteHandoverPlanId;
        //     return itemKey !== recordKey;
        //   })
        //   .map(item => String(item.productCode))
        //   .filter(Boolean);


        // Map options, disable if already selected
        const optionsWithDisabled = wasteOptions.map(opt => ({
          ...opt,
          // disabled: selectedProductCodes.includes(String(opt.value)),
        }));
        return (
          <Select
            style={{ width: '100%' }}
            placeholder={t('place-holder.select')}
            options={optionsWithDisabled}
            onChange={(value) => {
              const selectedOption = wasteOptions.find(opt => String(opt.value) === String(value));
              if (selectedOption && record && recordKey) {
                // Tìm tên contract từ contractOptionsMap dựa trên wasteGroupingContractId
                let contractName = '';
                if (selectedOption.wasteGroupingContractId && contractOptionsMap.size > 0) {
                  contractName = contractOptionsMap.get(selectedOption.wasteGroupingContractId) || '';
                }

                const formValues = editTableForm.getFieldsValue();
                const currentFormValue = formValues[recordKey] || {};

                // Cập nhật form values
                const updatedFormValue = {
                  ...currentFormValue,
                  productCode: selectedOption.value,
                  materialName: selectedOption.productName || selectedOption.label,
                  productName: selectedOption.productName || selectedOption.label,
                  wasteGroupingContractId: selectedOption.wasteGroupingContractId,
                  wasteGroupingContractName: contractName,
                };

                editTableForm.setFieldsValue({
                  [recordKey]: updatedFormValue
                });

                // Cập nhật dataSource để UI update ngay
                const updatedDataSource = dataSource.map(item => {
                  const itemKey = (item as any)?.wasteHandoverRegistrationDetailId || item.wasteHandoverPlanId;
                  return itemKey === recordKey
                    ? {
                      ...item,
                      productCode: selectedOption.value,
                      materialName: selectedOption.productName || selectedOption.label,
                      productName: selectedOption.productName || selectedOption.label,
                      wasteGroupingContractId: selectedOption.wasteGroupingContractId,
                      wasteGroupingContractName: contractName,
                    }
                    : item;
                });
                setDataTable(updatedDataSource);
              }
            }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
              (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
      render: (_, record) => {
        const productCode = record?.productCode ? String(record.productCode) : '';
        let materialName = '';

        if (record?.materialName) {
          materialName = record.materialName;
        } else if (productCode && wasteOptions.length > 0) {
          const wasteOption = wasteOptions.find(
            (opt) => String(opt.value) === productCode
          );
          if (wasteOption) {
            materialName = wasteOption.productName || wasteOption.label;
          }
        }
        if (!materialName && record?.productName) {
          materialName = typeof record.productName === 'string'
            ? record.productName
            : (record.productName as any)?.value || '';
        }

        if (productCode && materialName) {
          return <div>{`${productCode} | ${materialName}`}</div>;
        } else if (productCode) {
          return <div>{productCode}</div>;
        } else {
          return <div>-</div>;
        }
      },
    },
    // trọng lượng cân lần 1
    {
      title: t('waste-handover-plan-register.first-weight'),
      key: "firstWeight",
      dataIndex: "firstWeight",
      width: 150,
      className: 'min-w-[150px]',
      align: 'right',
      editable: false,
      render: (_, record) => {
        const value = (record as any)?.firstWeight;
        return value !== null && value !== undefined ? formatDecimalNumber(`${value}`) : '0.000';
      },
    },
    // trọng lượng cân lần 2
    {
      title: t('waste-handover-plan-register.second-weight'),
      key: "secondWeight",
      dataIndex: "secondWeight",
      width: 150,
      className: 'min-w-[150px]',
      align: 'right',
      editable: false,
      render: (_, record) => {
        const value = (record as any)?.secondWeight;
        return value !== null && value !== undefined ? formatDecimalNumber(`${value}`) : '0.000';
      },
    },
    // trọng lượng bì
    {
      title: 'Trọng lượng bì',
      key: "packingWeight",
      dataIndex: "packingWeight",
      width: 150,
      className: 'min-w-[150px]',
      align: 'right',
      editable: false,
      render: (_, record) => {
        const value = (record as any)?.packingWeight;
        return value !== null && value !== undefined ? formatDecimalNumber(`${value}`) : '0.000';
      },
    },
    // trọng lượng bàn giao thực tế
    {
      title: t('waste-handover-plan-register.actual-delivery-weight'),
      key: "deliveredQuantity",
      dataIndex: "deliveredQuantity",
      width: 180,
      className: 'min-w-[180px]',
      align: 'right',
      editable: false,
      render: (_, record) => {
        const value = (record as any)?.deliveredQuantity;
        return value !== null && value !== undefined ? formatDecimalNumber(`${value}`) : '0.000';
      },
    },
    {
      title: t('waste-handover-plan-register.unit'),
      key: "unit",
      dataIndex: "unit",
      width: 80,
      className: 'min-w-[80px]',
      readonly: true,
      renderFormItem(_, config) {
        const { record } = config;
        return <div>{(record as any)?.unit || t('waste-handover-plan-register.unit-default')}</div>
      },
      render: (_, record) => {
        return (record as any)?.unit || t('waste-handover-plan-register.unit-default');
      },
    },
    {
      title: t('waste-handover-plan-register.storage-location-name'),
      key: "storageLocationName",
      dataIndex: "storageLocationName",
      width: 160,
      className: 'min-w-[160px]',
      editable: (_, record) => {
        // Không cho sửa khu vực nếu item đã có wasteHandoverPlanId hợp lệ (từ kế hoạch)
        return canEditItem(record);
      },
      renderFormItem: (_, config) => {
        const { record } = config;
        const recordKey = (record as any)?.wasteHandoverRegistrationDetailId || record?.wasteHandoverPlanId;

        return (
          <Select
            style={{ width: '100%' }}
            placeholder={t('place-holder.select')}
            options={storageOptions}
            onChange={(value) => {
              const selectedOption = storageOptions.find(opt => opt.value === value);
              if (selectedOption && record && recordKey) {
                const formValues = editTableForm.getFieldsValue();
                const currentFormValue = formValues[recordKey] || {};

                const updatedFormValue = {
                  ...currentFormValue,
                  storageLocation: selectedOption.value,
                  storageLocationName: selectedOption.label,
                };

                editTableForm.setFieldsValue({
                  [recordKey]: updatedFormValue
                });

                // Cập nhật dataSource để UI update ngay
                const updatedDataSource = dataSource.map(item => {
                  const itemKey = (item as any)?.wasteHandoverRegistrationDetailId || item.wasteHandoverPlanId;
                  return itemKey === recordKey
                    ? {
                      ...item,
                      storageLocation: selectedOption.value,
                      storageLocationName: selectedOption.label,
                    }
                    : item;
                });
                setDataTable(updatedDataSource);
              }
            }}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
              (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
      render: (_, record) => {
        return (record as any)?.storageLocationName || '-';
      },
    },
    // trạng thái bàn giao
    {
      title: t('waste-handover-plan-register.delivery-status'),
      key: "deliveryStatus",
      dataIndex: "deliveryStatus",
      width: 180,
      className: 'min-w-[180px]',
      align: 'center',
      editable: false,
      render: (_, record) => {
        const statusName = (record as any)?.deliveryStatusName || t('waste-handover-plan-register.delivery-status-not-delivered');
        const deliveryStatus = (record as any)?.deliveryStatus || '';

        // Xác định màu sắc dựa trên deliveryStatus
        let borderColor = '#d9d9d9'; // Mặc định: xám
        let textColor = '#262626'; // Mặc định: đen
        let bgColor = '#ffffff'; // Mặc định: trắng

        switch (deliveryStatus) {
          case 'DBG': // Đã bàn giao
            borderColor = '#389e0d';
            textColor = '#389e0d';
            bgColor = '#f6ffed';
            break;
          case 'TCBG': // Từ chối bàn giao
            borderColor = '#ef4444';
            textColor = '#ef4444';
            bgColor = '#fff1f0';
            break;
          case 'CBG': // Chưa bàn giao
          default:
            borderColor = '#d9d9d9';
            textColor = '#262626';
            bgColor = '#fafafa';
            break;
        }

        return (
          <div className="flex justify-center">
            <div
              className="rounded px-3 py-1.5 text-center w-[200px]"
              style={{
                border: `1px solid ${borderColor}`,
                color: textColor,
                backgroundColor: bgColor,
              }}
            >
              {statusName}
            </div>
          </div>
        );
      },
    },
    {
      title: t('waste-handover-plan-register.note'),
      key: "note",
      dataIndex: "note",
      width: 200,
      className: 'min-w-[200px]',
      // Điều kiện sửa ghi chú giống nút "Thêm 1 hàng dữ liệu"
      editable: () => !disabledAll && isThemSuaChatThai,
      renderFormItem: (_, config) => {
        const { record } = config;
        return (
          <Input
            style={{ width: '100%' }}
            placeholder={t('waste-handover-plan-register.note')}
            value={(record as any)?.note}
          />
        );
      },
      render: (_, record) => {
        return (record as any)?.note || '-';
      },
    },
    {
      title: '',
      key: "action",
      dataIndex: "action",
      width: 60,
      className: 'min-w-[60px]',
      align: 'center',
      fixed: 'right',
      editable: false,
      render: (_, record) => {
        // Không hiển thị nút xóa nếu không có quyền
        if (!isThemSuaChatThai || disabledAll) return null;
        // Không hiển thị nút xóa nếu trạng thái là "Đã bàn giao" (DBG) hoặc "Từ chối bàn giao" (TCBG)
        const deliveryStatus = (record as any)?.deliveryStatus || '';
        if (deliveryStatus === 'DBG' || deliveryStatus === 'TCBG') return null;

        const handleDelete = () => {
          // Lấy key đúng theo rowKey function: ưu tiên wasteHandoverRegistrationDetailId, sau đó wasteHandoverPlanId
          const recordKey = (record as any)?.wasteHandoverRegistrationDetailId || record.wasteHandoverPlanId;
          if (!recordKey) return;

          const productCode = (record as any)?.productCode || '';
          const productName = (record as any)?.materialName || (record as any)?.productName || '';
          const wasteLabel = productCode && productName
            ? `${productCode} | ${productName}`
            : (productCode || productName || '');

          Modal.confirm({
            title: t('message.confirm-delete'),
            content: wasteLabel
              ? t('message.confirm-delete-content', { productInfo: wasteLabel })
              : t('message.confirm-delete-content', { productInfo: 'dòng này' }),
            okText: t('message.ok'),
            cancelText: t('message.cancel'),
            okType: 'danger',
            maskClosable: true,
            onOk: () => {
              // Xóa khỏi dataSource: tìm theo cả wasteHandoverRegistrationDetailId và wasteHandoverPlanId
              const newDataTable = dataSource.filter(item => {
                const itemKey = (item as any)?.wasteHandoverRegistrationDetailId || item.wasteHandoverPlanId;
                return itemKey !== recordKey;
              });
              setDataTable(newDataTable);

              // Xóa khỏi form: xóa theo key đúng
              const formValues = editTableForm.getFieldsValue();
              delete formValues[recordKey];
              // Nếu recordKey là wasteHandoverRegistrationDetailId, cũng cần xóa theo wasteHandoverPlanId nếu có
              if ((record as any)?.wasteHandoverRegistrationDetailId && record.wasteHandoverPlanId) {
                delete formValues[record.wasteHandoverPlanId];
              }
              editTableForm.setFieldsValue(formValues);
            },
          });
        };

        return (
          <div className="flex justify-center items-center">
            <FaTrash
              className="text-red-500 cursor-pointer hover:text-red-700 transition-colors"
              onClick={handleDelete}
              size={18}
            />
          </div>
        );
      },
    },
  ];

  return (
    <CardBox title={t('waste-handover-plan-register.goods-info')} className="col-span-2">
      <EditableProTable<WasteHandoverPlanModel>
        columns={columns}
        dataSource={dataSource}
        value={dataSource}
        onChange={(newDataSource) => {
          setDataTable(newDataSource as WasteHandoverPlanModel[]);
          // Đồng bộ form values khi thêm dòng mới
          const newDataArray = newDataSource as WasteHandoverPlanModel[];
          const formValues = editTableForm.getFieldsValue();
          const newFormValues: any = { ...formValues };

          newDataArray.forEach((row) => {
            // Dùng key giống rowKey function: ưu tiên wasteHandoverRegistrationDetailId, sau đó wasteHandoverPlanId
            const recordKey = (row as any)?.wasteHandoverRegistrationDetailId || row.wasteHandoverPlanId;
            if (!newFormValues[recordKey]) {
              // Khởi tạo form values cho dòng mới
              newFormValues[recordKey] = {
                ...row,
                actualHandoverQuantity: (row as any).actualHandoverQuantity || 0,
              };
            }
          });

          editTableForm.setFieldsValue(newFormValues);
        }}
        pagination={false}
        className={`${uiStore.theme.name}` + customTableClassName}
        editable={{
          type: 'multiple',
          form: editTableForm,
          editableKeys: disabledAll
            ? []
            : (dataSource ? dataSource.map(detail => (detail as any).wasteHandoverRegistrationDetailId || detail.wasteHandoverPlanId) : undefined),
        }}
        scroll={{ x: 800 }}
        rowKey={(record) => (record as any).wasteHandoverRegistrationDetailId || record.wasteHandoverPlanId}
        recordCreatorProps={isThemSuaChatThai && !disabledAll ? {
          newRecordType: 'dataSource',
          record: (index: number, dataSource: WasteHandoverPlanModel[]) => {
            const detailId = nanoid(); // Tạo nanoid cho wasteHandoverRegistrationDetailId

            const nextStt = (() => {
              if (!dataSource || dataSource.length === 0) return 1;
              const maxStt = Math.max(
                ...dataSource
                  .map((x: any) => Number(x.stt) || 0)
              );
              return (isFinite(maxStt) ? maxStt : 0) + 1;
            })();

            return {
              // Không tạo wasteHandoverPlanId cho item mới (để null hoặc empty)
              wasteHandoverPlanId: '',
              wasteHandoverRegistrationDetailId: detailId, // Gán nanoid cho item mới
              stt: nextStt,
              productCode: '',
              materialName: '',
              productName: '',
              storageLocation: '',
              storageLocationName: '',
              plannedQuantity: 0,
              actualHandoverQuantity: 0,
              openQuantity: 0,
              firstWeight: 0,
              secondWeight: 0,
              actualDeliveryWeight: 0,
              unit: 'KG',
              deliveryStatus: '',
              deliveryStatusName: '',
              note: '',
              deliveryDate: '',
              wasteHandoverPlanCode: '',
              vendorNumber: '',
              vendorName: '',
              handoverDocumentNumber: '',
              saleOrgCode: '',
              actived: true,
              createTime: '',
            } as WasteHandoverPlanModel;
          },
          creatorButtonText: t('waste-handover-plan.add-item'),
        } : false}
        bordered
      />
    </CardBox>
  );
};

export default HandoverInfoEdit;

